/**
 * Zákazníci API — typed wrappers around `/v1/customers`.
 *
 * Mirrors the OpenAPI shapes (`CustomerListResponseSchema`,
 * `CustomerDetailResponseSchema`, `CustomerSummaryResponseSchema`) in
 * `api/src/schemas/customers.ts`. Re-declared here so the module compiles when
 * the gitignored `frontend/src/types/api.ts` hasn't been refreshed.
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';

export interface Customer {
  id_zakaznika: number;
  jmeno: string;
  ulice: string;
  cislo_popisne: string;
  psc: string;
  mesto: string;
  ico: string;
  dico: string;
  zastupce: string;
  telefon: string;
  email: string;
  /** Bank details for returning the rental deposit — null when none on file. */
  ucet_predcisli: string | null;
  ucet_cislo: string | null;
  ucet_kod_banky: string | null;
  iban: string | null;
  id_firmy: number;
}

export interface CustomerListFilters {
  search?: string;
  /** 'name' matches the customer name only (skips the zastupce field). */
  match?: 'name';
  ico?: string;
  limit?: string;
  offset?: string;
}

interface CustomerListResponse {
  customers: Customer[];
  total: number;
}

function buildQuery(filters: CustomerListFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value != null && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useCustomersQuery(
  filters: CustomerListFilters,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['customers', filters],
    enabled: options?.enabled ?? true,
    queryFn: ({ signal }) =>
      api.get<CustomerListResponse>(`/v1/customers${buildQuery(filters)}`, { signal }),
    placeholderData: (previous) => previous,
  });
}

/**
 * Shared customer-picker state: a search-driven page that can grow on demand.
 * Every picker modal/wizard step renders at most `pageSize` rows by default —
 * this surfaces the response `total` and a `loadMore` so matches beyond the
 * cap are reachable (legacy pickers listed every match). The limit resets when
 * the search changes; pass `enabled: false` while the picker is closed so a
 * merely-mounted modal fires no query at all.
 */
export function useCustomerPicker(
  search: string,
  options?: { enabled?: boolean; pageSize?: number },
) {
  const pageSize = options?.pageSize ?? 20;
  const [limit, setLimit] = useState(pageSize);
  useEffect(() => setLimit(pageSize), [search, pageSize]);
  const query = useCustomersQuery(
    { search, limit: String(limit) },
    { enabled: options?.enabled ?? true },
  );
  const customers = query.data?.customers ?? [];
  const total = query.data?.total ?? 0;
  const remaining = Math.max(0, total - customers.length);
  return {
    query,
    customers,
    total,
    remaining,
    hasMore: remaining > 0,
    loadMore: () => setLimit((l) => l + pageSize * 5),
  };
}

export function useCustomersInfiniteQuery(
  filters: Omit<CustomerListFilters, 'limit' | 'offset'>,
  options?: { pageSize?: number },
) {
  const pageSize = options?.pageSize ?? 300;
  return useInfiniteQuery({
    queryKey: ['customers', 'infinite', filters, pageSize],
    queryFn: ({ pageParam, signal }) =>
      api.get<CustomerListResponse>(
        `/v1/customers${buildQuery({ ...filters, limit: String(pageSize), offset: String(pageParam) })}`,
        { signal },
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((acc, p) => acc + p.customers.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: (previous) => previous,
  });
}

// ==================== Search (Vyhledávání subtab) ====================

export type CustomerSearchContractType = 'any' | 'vozidla' | 'ojeta' | 'vykup' | 'operak';
export type CustomerSearchContractKind = Exclude<CustomerSearchContractType, 'any'>;

export interface CustomerSearchContract {
  typ: CustomerSearchContractKind;
  id_smlouvy: number;
  /** ISO YYYY-MM-DD close date. */
  datum: string;
  cena_celkem: string;
  /** Brand + model (sales) or typ_vozu (lease). */
  popis: string;
}

export type CustomerSearchSmer = 'starsi' | 'novejsi';

export interface CustomerSearchRow extends Customer {
  /** Matching contracts, newest first (full history for 'starsi', the window for 'novejsi'). */
  smlouvy: CustomerSearchContract[];
  smluv_celkem: number;
  posledni_smlouva: string;
}

export interface CustomerSearchFilters {
  search?: string;
  smlouva?: CustomerSearchContractType;
  /**
   * 'starsi' (server default): newest contract at least `mesice` months old
   * (lapsed buyers). 'novejsi': a contract within the last `mesice` months.
   */
  smer?: CustomerSearchSmer;
  /** Boundary in months (server default 36). */
  mesice?: number;
}

export interface CustomerSearchResponse {
  customers: CustomerSearchRow[];
  total: number;
  /** ISO boundary date (today minus `mesice` months), echoed by the server. */
  hranice: string;
  smer: CustomerSearchSmer;
}

function buildSearchQuery(f: CustomerSearchFilters, limit: number, offset: number): string {
  const params = new URLSearchParams();
  if (f.search) params.set('search', f.search);
  if (f.smlouva && f.smlouva !== 'any') params.set('smlouva', f.smlouva);
  if (f.smer) params.set('smer', f.smer);
  if (f.mesice != null) params.set('mesice', String(f.mesice));
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  return `?${params.toString()}`;
}

/** `GET /v1/customers/search` — customers by contract age (lapsed or recent buyers). */
export function useCustomerSearchInfiniteQuery(
  filters: CustomerSearchFilters,
  options?: { pageSize?: number },
) {
  const pageSize = options?.pageSize ?? 100;
  return useInfiniteQuery({
    queryKey: ['customers', 'search', filters, pageSize],
    queryFn: ({ pageParam, signal }) =>
      api.get<CustomerSearchResponse>(
        `/v1/customers/search${buildSearchQuery(filters, pageSize, pageParam)}`,
        { signal },
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((acc, p) => acc + p.customers.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: (previous) => previous,
  });
}

interface CustomerDetailResponse {
  customer: Customer;
}

export function useCustomerDetailQuery(id: number | null) {
  return useQuery({
    queryKey: ['customers', id],
    enabled: id != null && Number.isFinite(id),
    // Backs the customer edit form — always refetch on mount so it opens against
    // fresh server state.
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      api.get<CustomerDetailResponse>(`/v1/customers/${id}`, { signal }),
  });
}

export interface VehicleRow {
  id_vozidla: number;
  vc_auta: string;
  spz_vozu: string;
  vin: string;
  barva: string;
  nazev_znacky: string | null;
  nazev_modelu: string | null;
  // Hidden — searchable only on the customer-detail page. All NOT NULL on `vozidlo`.
  motor: string;
  stav_vozidla: string;
  poznamka: string;
}

export interface VehicleOfferRow {
  id_nabidky: number;
  barva: string;
  motor: string;
  cena_celkem: string;
  nazev_znacky: string | null;
  nazev_modelu: string | null;
  // From the linked vehicle (LEFT JOIN), so null when no vehicle is linked.
  vc_auta: string | null;
  cislo_zakazky: string | null;
  // Hidden — searchable only.
  interni_poznamka: string | null;
}

export interface VehicleContractRow {
  id_smlouvy: number;
  barva: string;
  motor: string;
  cena_celkem: string;
  stav_smlouvy: string;
  nazev_znacky: string | null;
  nazev_modelu: string | null;
  // From the linked vehicle (LEFT JOIN), so null when no vehicle is linked.
  vc_auta: string | null;
  cislo_zakazky: string | null;
  // Hidden — searchable only.
  interni_poznamka: string | null;
}

export interface UsedVehicleRow {
  id_vozidla: number;
  spz_vozu: string;
  vin: string;
  barva: string;
  nazev_znacky: string | null;
  nazev_modelu: string | null;
  // Hidden — searchable only. All NOT NULL on `ojete_vozidlo`.
  motor: string;
  stav_vozidla: string;
  poznamka: string;
}

export interface UsedVehicleOfferRow {
  id_nabidky: number;
  barva: string;
  motor: string;
  cena_celkem: string | null;
  nazev_znacky: string | null;
  nazev_modelu: string | null;
  // Hidden — searchable only.
  interni_poznamka: string | null;
}

export interface UsedVehicleContractRow {
  id_smlouvy: number;
  barva: string;
  motor: string;
  cena_celkem: string;
  stav_smlouvy: string;
  nazev_znacky: string | null;
  nazev_modelu: string | null;
  // Hidden — searchable only.
  interni_poznamka: string | null;
}

export interface BuybackContractRow {
  id_smlouvy: number;
  spz_vozu: string;
  cena_celkem: string;
  nazev_znacky: string | null;
  nazev_modelu: string | null;
  // Hidden — searchable only. motor/stav_smlouvy are NOT NULL on the table;
  // interni_poznamka comes via a LEFT JOIN so it can be null.
  motor: string;
  stav_smlouvy: string;
  interni_poznamka: string | null;
}

export interface LeasingRow {
  id_smlouvy: number;
  typ_vozu: string;
  barva: string;
  cena_celkem: string;
  splatka: number;
  // Hidden — searchable only. `dop_text` exists on contracts only (offers have
  // no supplementary-text column), so it's optional on the shared row type.
  motor: string;
  provedeni: string;
  verze: string;
  dop_text?: string;
}

export interface ContactRow {
  id_kontaktu: number;
  jmeno_klienta: string | null;
  spolecnost: string;
  typ_kontaktu: number;
  podpis_objednavky: string | null;
  automobil: number;
  znacka_vozu: number;
  nazev_znacky: string | null;
  lms_nazev: string | null;
  rok_uvedeni: number | null;
  nazev_modelu: string | null;
  // Hidden — searchable only.
  status: string | null;
  poznamka: string | null;
  vysledek: string | null;
}

export interface CustomerSummary {
  vehicles: VehicleRow[];
  vehicleOffers: VehicleOfferRow[];
  vehicleContracts: VehicleContractRow[];
  usedVehicles: UsedVehicleRow[];
  usedVehicleOffers: UsedVehicleOfferRow[];
  usedVehicleContracts: UsedVehicleContractRow[];
  buybackContracts: BuybackContractRow[];
  leasingOffers: LeasingRow[];
  leasingContracts: LeasingRow[];
  contacts: ContactRow[];
}

export function useCustomerSummaryQuery(id: number | null) {
  return useQuery({
    queryKey: ['customers', id, 'summary'],
    enabled: id != null && Number.isFinite(id),
    queryFn: ({ signal }) =>
      api.get<CustomerSummary>(`/v1/customers/${id}/summary`, { signal }),
  });
}

export interface CustomerBody {
  jmeno?: string;
  ulice?: string;
  cislo_popisne?: string;
  psc?: string;
  mesto?: string;
  ico?: string;
  dico?: string;
  zastupce?: string;
  telefon?: string;
  email?: string;
  ucet_predcisli?: string;
  ucet_cislo?: string;
  ucet_kod_banky?: string;
  iban?: string;
  link_type?: string;
  link_id?: number | string;
}

interface CustomerCreatedResponse {
  status: 'ok';
  id_zakaznika: number;
}

export function useCreateCustomerMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CustomerBody) =>
      api.post<CustomerCreatedResponse>('/v1/customers', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export function useUpdateCustomerMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CustomerBody) =>
      api.put<{ status: 'ok' }>(`/v1/customers/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      qc.invalidateQueries({ queryKey: ['customers', id] });
    },
  });
}

export interface DuplicateMatch {
  id_zakaznika: number;
  jmeno: string;
  ico: string;
  dico: string;
  email: string;
  telefon: string;
  ulice: string;
  cislo_popisne: string;
  mesto: string;
  zastupce: string;
  child_total: number;
}

export interface DuplicatesResponse {
  exact_ico_match: DuplicateMatch | null;
  email_matches: DuplicateMatch[];
  phone_matches: DuplicateMatch[];
  name_matches: DuplicateMatch[];
}

export interface DuplicatesPayload {
  ico?: string;
  email?: string;
  telefon?: string;
  jmeno?: string;
  ulice?: string;
  cislo_popisne?: string;
  mesto?: string;
  zastupce?: string;
}

export function useDuplicatesQuery(payload: DuplicatesPayload, enabled: boolean) {
  return useQuery({
    queryKey: ['customers', 'duplicates', payload],
    enabled,
    queryFn: ({ signal }) =>
      api.post<DuplicatesResponse>('/v1/customers/check-duplicates', payload, {
        signal,
      }),
    staleTime: 0,
    placeholderData: (previous) => previous,
  });
}

/** Imperative one-shot — used by the submit flow (the React-query hook handles the live aside). */
export function checkDuplicates(
  payload: DuplicatesPayload,
  signal?: AbortSignal,
): Promise<DuplicatesResponse> {
  return api.post<DuplicatesResponse>('/v1/customers/check-duplicates', payload, {
    signal,
  });
}

export type CustomerLinkType =
  | 'vozidla_smlouva'
  | 'ojeta_vozidla_smlouva'
  | 'ojeta_vozidla_vykup_smlouva'
  | 'vozidla_nabidka'
  | 'ojeta_vozidla_nabidka'
  | 'operak_nabidka'
  | 'operak_smlouva'
  | 'vozidlo'
  | 'ojete_vozidlo';

/** Czech labels for the link entity types — used in LinkedContext + Savebar copy. */
export const CUSTOMER_LINK_LABEL: Record<CustomerLinkType, string> = {
  vozidla_smlouva: 'Smlouva — Vozidla',
  ojeta_vozidla_smlouva: 'Smlouva — Ojetá vozidla',
  ojeta_vozidla_vykup_smlouva: 'Výkup — Ojetá vozidla',
  vozidla_nabidka: 'Nabídka — Vozidla',
  ojeta_vozidla_nabidka: 'Nabídka — Ojetá vozidla',
  operak_nabidka: 'Nabídka — Operativní leasing',
  operak_smlouva: 'Smlouva — Operativní leasing',
  vozidlo: 'Vozidlo',
  ojete_vozidlo: 'Ojeté vozidlo',
};

export function useLinkCustomerMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id_zakaznika,
      link_type,
      link_id,
    }: {
      id_zakaznika: number;
      link_type: CustomerLinkType;
      link_id: number;
    }) =>
      api.post<{ status: 'ok'; id_zakaznika: number }>(
        `/v1/customers/${id_zakaznika}/link`,
        { link_type, link_id },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export interface CustomerCompany {
  id_firmy: number;
  Nazev: string | null;
}

/**
 * Companies the current user can access (`GET /v1/customers/companies`) —
 * the target list for the customer copy/duplicate flow on the detail page.
 */
export function useCustomerCompaniesQuery(enabled = true) {
  return useQuery({
    queryKey: ['customers', 'companies'],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: ({ signal }) =>
      api.get<{ companies: CustomerCompany[] }>('/v1/customers/companies', { signal }),
  });
}

/**
 * Customers in another accessible company that look like copy targets
 * (`GET /v1/customers/copy-candidates`). Used by the leasing contract-copy
 * flow to resolve which customer the copied contract should bind to in the
 * target company. `companyId` is the target company; `search` filters by name
 * (the endpoint also folds in an exact-IČO match).
 */
export function useCopyCandidatesQuery(
  companyId: number | null,
  search: string,
  enabled: boolean,
  ico = '',
) {
  return useQuery({
    queryKey: ['customers', 'copy-candidates', companyId, search, ico],
    enabled: enabled && companyId != null,
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({
        company_id: String(companyId),
        limit: '50',
      });
      if (search.trim()) params.set('search', search.trim());
      // Exact-IČO match OR'd in server-side — both are sent so a renamed customer with the same IČO still surfaces.
      if (ico.trim()) params.set('ico', ico.trim());
      return api.get<CustomerListResponse>(
        `/v1/customers/copy-candidates?${params.toString()}`,
        { signal },
      );
    },
    placeholderData: (previous) => previous,
  });
}

/**
 * Copy a customer into another company (`POST /v1/customers/{id}/copy`). Legacy
 * "Nový z původního" — clones the source customer row into `id_firmy` and
 * returns the new id so a contract can bind to it.
 */
export function useCopyCustomerMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, id_firmy }: { id: number; id_firmy: number }) =>
      api.post<{ status: 'ok'; id_zakaznika: number }>(
        `/v1/customers/${id}/copy`,
        { id_firmy },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] }),
  });
}

export interface AresResult {
  stav: 'ok';
  ico: string;
  dic?: string;
  firma: string;
  ulice?: string;
  cislo_popisne?: string;
  mesto?: string;
  psc?: number;
}

export interface AresError {
  stav: string;
}

export type AresResponse = AresResult | AresError;

export function isAresOk(r: AresResponse): r is AresResult {
  return r.stav === 'ok';
}

export function lookupAres(ico: string, signal?: AbortSignal): Promise<AresResponse> {
  return api.get<AresResponse>(`/v1/ares/${encodeURIComponent(ico)}`, { signal });
}
